/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useUniqueId } from '@squiz/dxp-console-support';
import clsx from 'clsx';
import FocusTrap from 'focus-trap-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DXP_DATA_ID_DIALOG } from '../../Constants/DataIds';
import { ICON_ASPECT_RATIO, ICON_CLOSE, ICON_FULLSCREEN_EXIT } from '../../Icons';
import { ButtonList } from '../ButtonList';
import { ErrorMessage } from '../ErrorMessage';
import { Loader } from '../Loader';
import { SdsIcon } from '../SdsIcon';
import { SdsTransition, SdsTransitionProps, TRANSITION_TYPE_FADE } from '../SdsTransition';
import { SPINNER_TYPE_REGULAR } from '../Spinner';
import { DialogContentProps, DialogProps, dialogDefaults } from './Dialog.props';
import {
  WIZARD_DIRECTION_NEXT,
  WIZARD_DIRECTION_PREVIOUS,
  WizardDirectionType,
  WizardStep,
  WizardStepProps,
} from './Wizard';
import { DIALOG_TYPE_WIZARD } from './types/DialogType.types';

/**
 * SDS Dialog component.
 */
export const Dialog = (props: DialogProps): JSX.Element => {
  const {
    buttonKeys,
    buttons,
    classes,
    closeButtonText,
    dialogContent,
    dialogSize,
    dialogType,
    errorMessage,
    footer,
    heading,
    icon,
    loading,
    loadingMessage,
    onClose,
    preventFullScreen,
    showFullScreenButton,
    scrollOnOverflow,
    showDividers,
    stateHandler,
    subtitle,
    wizardSteps,
    ...rest
  } = {
    ...dialogDefaults,
    ...props,
  };

  const dialogId = useUniqueId();
  const defaultStep = { customProps: {}, stepNumber: 0 };
  const dialogRef = useRef<HTMLDivElement>(null);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [removeGlobalDialog, setRemoveGlobalDialog] = useState<boolean>(false);
  const [showFullscreen, setShowFullscreen] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState(defaultStep);
  const [customClose, setCustomClose] = useState<(() => void) | undefined>(() => onClose);
  const [actionButtonKeys, setActionButtonKeys] = useState<Array<string>>(buttonKeys);
  const [actionButtons, setActionButtons] = useState<Array<JSX.Element>>(buttons);
  const { activeDialog, updateActiveDialog } = stateHandler();

  const stepNumberRef = useRef<number>(0);
  const stepsRef = useRef<Array<WizardStep>>([]);

  const closeHandler = (): void => {
    customClose && customClose();
    setRemoveGlobalDialog(true);
  };

  const hideDialog = useCallback((): void => {
    setShowDialog(false);
  }, [setShowDialog]);

  const handleFullscreen = (): void => {
    setShowFullscreen(!showFullscreen);
  };

  const dialogClass = clsx(
    `sds-dialog`,
    {
      [`sds-wizard`]: dialogType === DIALOG_TYPE_WIZARD,
    },
    showFullscreen && 'fullscreen',
    dialogSize,
    classes,
  );
  const dialogContainerClass = clsx(`sds-dialog__container`, {
    [`sds-dialog__container--full`]: !preventFullScreen,
    [`sds-dialog__container--sm`]: preventFullScreen,
  });
  const dialogBodyClass = clsx(`sds-dialog__body`, {
    [`dialog--scroll`]: scrollOnOverflow,
    [`dividers`]: showDividers,
  });

  const dialogContentProps = useMemo((): DialogContentProps => {
    return {
      close: hideDialog,
      setAfterClose: setCustomClose,
      setButtonKeys: setActionButtonKeys,
      setButtons: setActionButtons,
    };
  }, [hideDialog, setActionButtons]);

  //
  // Wizard Configuration
  //

  if (dialogType === DIALOG_TYPE_WIZARD) {
    stepNumberRef.current = currentStep.stepNumber;
    stepsRef.current = wizardSteps as Array<WizardStep>;
  }

  const updateStep = useCallback((direction: WizardDirectionType, customProps = {}): void => {
    const maxIndex = stepsRef.current.length - 1;

    if (direction === WIZARD_DIRECTION_NEXT) {
      if (stepNumberRef.current < maxIndex) {
        setCurrentStep({ customProps, stepNumber: stepNumberRef.current + 1 });
      }
    } else if (direction === WIZARD_DIRECTION_PREVIOUS) {
      if (stepNumberRef.current > 0) {
        setCurrentStep({ customProps, stepNumber: stepNumberRef.current - 1 });
      }
    }
  }, []);

  // Set up steps
  const wizardStepsProps = useMemo((): WizardStepProps => {
    return {
      close: hideDialog,
      customProps: currentStep.customProps,
      setAfterClose: setCustomClose,
      setButtonKeys: setActionButtonKeys,
      setButtons: setActionButtons,
      updateStep: updateStep,
    };
  }, [currentStep, hideDialog, setActionButtons, updateStep]);

  let defaultContent = <></>;

  if (dialogType === DIALOG_TYPE_WIZARD) {
    const steps = wizardSteps as Array<WizardStep>;
    defaultContent = steps[currentStep.stepNumber].renderStep(wizardStepsProps);
  }

  const [content, setContent] = useState<JSX.Element>(defaultContent);

  // Special effect call for managing global state.
  // When the dialog has finished closing,
  // And the global dialog still exists,
  // Set the global dialog to null
  useEffect(() => {
    if (removeGlobalDialog && activeDialog) {
      updateActiveDialog(null);
    }
  }, [activeDialog, removeGlobalDialog, updateActiveDialog]);

  useEffect(() => {
    let newDialogContent = <></>;

    if (dialogType === DIALOG_TYPE_WIZARD) {
      const steps = wizardSteps as Array<WizardStep>;

      if (steps.length > 0) {
        // Render content of new step
        newDialogContent = steps[currentStep.stepNumber].renderStep(wizardStepsProps);
      }
    } else {
      newDialogContent = dialogContent.renderContent(dialogContentProps);
    }

    if (newDialogContent.type !== content.type) {
      setContent(newDialogContent);
    }
  }, [content, currentStep, dialogContent, dialogContentProps, dialogType, setContent, wizardSteps, wizardStepsProps]);

  useEffect(() => {
    const handleGeneralClick = (event: MouseEvent): void => {
      const targetEvent = event.target as HTMLElement;

      // Listen for a click in the container itself, not the dialog
      if (targetEvent.classList.contains(`sds-dialog__container`)) {
        hideDialog();
      }
    };

    const handleKeydown = (event: KeyboardEvent): void => {
      const keyCode = event.code;

      if (keyCode === `Escape`) {
        hideDialog();
      }
    };

    setShowDialog(true);
    document.addEventListener('mousedown', handleGeneralClick);
    document.addEventListener('keydown', handleKeydown);

    return (): void => {
      document.removeEventListener('mousedown', handleGeneralClick);
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [hideDialog, setShowDialog]);

  const dialog = (
    <FocusTrap>
      <div className={dialogContainerClass} data-active={showDialog} data-id={DXP_DATA_ID_DIALOG} {...rest}>
        <div
          aria-labelledby={`sds-dialog-title--${dialogId}`}
          aria-modal="true"
          className={dialogClass}
          ref={dialogRef}
          role="dialog"
        >
          <div className="sds-dialog__header">
            <div className="sds-dialog__heading">
              {icon && (
                <div className="sds-dialog__icon">
                  <SdsIcon svg={icon} />
                </div>
              )}
              <span id={`sds-dialog-title--${dialogId}`}>{heading}</span>
            </div>
            {subtitle && (
              <div className="sds-dialog__subtitle">
                <span>{subtitle}</span>
              </div>
            )}
            <div className="sds-dialog__action-buttons">
              {showFullScreenButton && (
                <button
                  className="sds-dialog__fullscreen"
                  onClick={(event) => {
                    event.preventDefault();
                    handleFullscreen();
                  }}
                  type="button"
                >
                  <span className="sr-only">Fullscreen this dialog</span>
                  <SdsIcon svg={showFullscreen ? ICON_FULLSCREEN_EXIT : ICON_ASPECT_RATIO} />
                </button>
              )}
              <button
                className="sds-dialog__close"
                onClick={(event): void => {
                  event.preventDefault();
                  hideDialog();
                }}
                tabIndex={0}
              >
                <span className="sr-only">Close this dialog</span>
                <SdsIcon svg={ICON_CLOSE} />
              </button>
            </div>
          </div>
          {loading ? (
            <div className="sds-dialog__loading">
              <div className={dialogBodyClass}>
                <Loader message={loadingMessage} type={SPINNER_TYPE_REGULAR} />
              </div>
            </div>
          ) : (
            <>
              <div className={dialogBodyClass}>
                <div className="sds-dialog__content">{content}</div>
              </div>
              <div className="sds-dialog__footer">
                {errorMessage && (
                  <div className="sds-dialog__error">
                    <ErrorMessage message={errorMessage} />
                  </div>
                )}
                {footer && (
                  <div className="sds-dialog__footer-text">
                    <span>{footer}</span>
                  </div>
                )}
                <div className="sds-dialog__actions">
                  <ButtonList buttonKeys={actionButtonKeys} buttons={actionButtons} />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </FocusTrap>
  );

  const transitionProps = {
    exitCallback: closeHandler,
    isActive: showDialog,
    transitionType: TRANSITION_TYPE_FADE,
  } as SdsTransitionProps;

  return <SdsTransition {...transitionProps}>{dialog}</SdsTransition>;
};
